import { Children } from "react";
import { formatString } from "@format-string/core";
import { Box, LinearProgress } from "@mui/material";
import { Document, PDFViewer, View } from "@react-pdf/renderer";
import { PDFCabeçalho } from "src/Pages/components/PDF/Cabeçalho";
import { ItemMapToInfoSearch } from "src/Pages/components/PDF/ItemMapToInfoSearch";
import { LineItem } from "src/Pages/components/PDF/LineItem/LineItem";
import { NumberPages } from "src/Pages/components/PDF/NumberPages";
import { PageCorePDF } from "src/Pages/components/PDF/PageCorePDF";
import { TableBase } from "src/Pages/components/PDF/TableBase";
import { PDFTableRow } from "src/Pages/components/PDF/TableRow";
import { TableRowContainer } from "src/Pages/components/PDF/TableRowContainer";
import { FormatDateBR, GetDateUSSemValue } from "src/shared/Utils/FormatDateBR";
import { FormatMoney } from "src/shared/Utils/FormatMoney";
import { handleFormatDateIfIsNull } from "src/shared/Utils/handleFormatDateIfIsNull";
import { HandleGetHoursCurrency } from "src/shared/Utils/HandleGetHoursCurrency";
import { handleIfLimitCharacter } from "src/shared/Utils/LimitText";

import {
  IReportContractsToInstallmentsFilters,
  IReportContractsToInstallmentsResponse,
} from "../../types";
import { HeadReportContractsToInstallmentsPDF } from "./HeadReportContractsToInstallmentsPDF";

interface IReportContractsToInstallmentsPDFProps {
  report?: IReportContractsToInstallmentsResponse;
  filters: IReportContractsToInstallmentsFilters;
  loading: boolean;
}

const getCpfCnpjMasked = (value: string) => {
  if (!value) return "";
  if (value.length <= 11) return formatString({ type: "cpf", value }) as string;
  return formatString({ type: "cnpj", value }) as string;
};

const typeDateLabel = {
  VIGENCIA: "Vigência",
  ASSINATURA: "Assinatura",
  EMISSAO: "Emissão",
  VENCIMENTO: "Vencimento",
};

export const ReportContractsToInstallmentsPDF = ({
  report,
  filters,
  loading,
}: IReportContractsToInstallmentsPDFProps) => {
  const data = report?.data;

  return (
    <>
      {!!data?.length && (
        <PDFViewer style={{ height: "84vh" }} width="100%">
          <Document creator="Brokeris" title="Relatório contrato com parcelas">
            <PageCorePDF orientation="landscape" padding="16px 8px 30px 8px">
              <PDFCabeçalho
                dateCurrency={FormatDateBR(GetDateUSSemValue())}
                empresa=""
                endDate={FormatDateBR(filters.end_date)}
                hoursCurrency={HandleGetHoursCurrency()}
                nomePdf="Relatório contrato com parcelas"
                startDate={FormatDateBR(filters.start_date)}
              />

              <View
                fixed
                style={{ borderBottom: "1px solid black", marginBottom: "14px" }}
              >
                <ItemMapToInfoSearch
                  itens={[typeDateLabel[filters.type_date]]}
                  title="Tipo de data"
                />
                <ItemMapToInfoSearch
                  itens={filters.nome ? [filters.nome] : []}
                  title="Nome"
                />
                <ItemMapToInfoSearch
                  itens={filters.cpf_cnpj ? [filters.cpf_cnpj] : []}
                  title="CPF/CNPJ"
                />
                <ItemMapToInfoSearch
                  itens={filters.cod_proposta ? [filters.cod_proposta] : []}
                  title="Cód. proposta"
                />
              </View>

              <TableBase margin="0">
                {Children.toArray(
                  data.map(({ contrato }) => (
                    <>
                      <LineItem
                        id={`Contrato:  ${contrato.cod_proposta}`}
                        name={` |  Cliente: ${handleIfLimitCharacter({
                          value: contrato.cliente.nome,
                          LIMIT: 35,
                        })}  |  CPF/CNPJ: ${getCpfCnpjMasked(contrato.cliente.cpf_cnpj)}  |  Vendedor: ${handleIfLimitCharacter({
                          value: contrato.produtor?.name || "-",
                          LIMIT: 25,
                        })}  |  Produto: ${handleIfLimitCharacter({
                          value: contrato.produto || "-",
                          LIMIT: 28,
                        })}`}
                      />
                      <HeadReportContractsToInstallmentsPDF />
                      {Children.toArray(
                        contrato.parcelas.map((parcela) => (
                          <TableRowContainer key={parcela.id}>
                            <PDFTableRow
                              textAlign="center"
                              title={parcela.numero_parcela}
                              width="8%"
                            />
                            <PDFTableRow
                              textAlign="center"
                              title={String(parcela.vidas ?? "-")}
                              width="8%"
                            />
                            <PDFTableRow
                              textAlign="right"
                              title={FormatDateBR(
                                handleFormatDateIfIsNull(parcela.data_vencimento)
                              )}
                              width="12%"
                            />
                            <PDFTableRow
                              textAlign="right"
                              title={FormatDateBR(
                                handleFormatDateIfIsNull(parcela.data_recebimento)
                              )}
                              width="12%"
                            />
                            <PDFTableRow
                              textAlign="center"
                              title={
                                (parcela.is_parcela_recebida ??
                                  parcela.is_parcelas_recebida)
                                  ? "Sim"
                                  : "Não"
                              }
                              width="10%"
                            />
                            <PDFTableRow
                              textAlign="right"
                              title={FormatMoney(parcela.valor_contrato)}
                              width="13%"
                            />
                            <PDFTableRow
                              textAlign="right"
                              title={parcela.is_parcela_recebida ? FormatMoney(parcela.valor_recebido || "0") : ""}
                              width="14%"
                            />
                            <PDFTableRow
                              textAlign="right"
                              title={
                                parcela.is_parcela_recebida
                                  ? parcela.valor_divergente_contrato_to_recebido && Number(parcela.valor_divergente_contrato_to_recebido) < 0 ? `-${FormatMoney(parcela.valor_divergente_contrato_to_recebido || "0")}` : FormatMoney(parcela.valor_divergente_contrato_to_recebido || "0")
                                  : ""
                              }
                              width="11%"
                            />
                            <PDFTableRow
                              paddingRight="8px"
                              textAlign="right"
                              title={Number(parcela.valor_lucro_parcela) < 0 ? `-${FormatMoney(parcela.valor_lucro_parcela)}` : FormatMoney(parcela.valor_lucro_parcela)}
                              width="12%"
                            />
                          </TableRowContainer>
                        ))
                      )}
                    </>
                  ))
                )}
              </TableBase>

              <NumberPages />
            </PageCorePDF>
          </Document>
        </PDFViewer>
      )}

      {loading && (
        <Box style={{ width: "100%" }}>
          <LinearProgress color="inherit" />
        </Box>
      )}
    </>
  );
};
